import React, { createContext, useEffect, useRef, useState } from "react";

const STICKY_GAP = 10;
const HEIGHT_BAR_PANE = 64;
const FALLBACK_STICKY_TOP = HEIGHT_BAR_PANE + STICKY_GAP;

export const PageStickyContext = createContext({
    pageBarStickyTop: null,
    pinnedPageBar: false,
    pinnedHeader: false
});

export const PageStickyProvider = ({ hasHeader, render }) => {
    const topSentinelRef = useRef(null);
    const headerBlockRef = useRef(null);
    const contentSentinelRef = useRef(null);
    const [pageBarStickyTop, setPageBarStickyTop] = useState(null);
    const [pinnedHeader, setPinnedHeader] = useState(false);
    const [pinnedPageBar, setPinnedPageBar] = useState(false);

    useEffect(() => {
        const handlePinnedHeader = ([entry]) => {
            if (!entry) return;
            setPinnedHeader(entry.intersectionRatio < 1);
        };

        const sentinel = topSentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(handlePinnedHeader, {
            threshold: 1,
            rootMargin: `-${HEIGHT_BAR_PANE}px 0px 0px 0px`
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handlePageBarStuck = ([entry]) => {
            if (!entry) return;
            setPinnedPageBar(!entry.isIntersecting);
        };

        const sentinel = contentSentinelRef.current;
        if (!sentinel) return;
        const stickyTop = pageBarStickyTop ?? FALLBACK_STICKY_TOP;
        const observer = new IntersectionObserver(handlePageBarStuck, {
            threshold: 0,
            rootMargin: `-${stickyTop}px 0px 0px 0px`
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [pageBarStickyTop]);

    useEffect(() => {
        const headerEl = headerBlockRef.current;
        const updateStickyTop = () => {
            const headerHeight = headerEl?.offsetHeight ?? 0;
            setPageBarStickyTop(HEIGHT_BAR_PANE + headerHeight + STICKY_GAP);
        };

        updateStickyTop();
        if (!headerEl) return;

        const resizeObserver = new ResizeObserver(updateStickyTop);
        resizeObserver.observe(headerEl);
        return () => resizeObserver.disconnect();
    }, [hasHeader]);

    const contextValue = { pageBarStickyTop, pinnedPageBar, pinnedHeader };

    return (
        <PageStickyContext.Provider value={contextValue}>
            {render({ topSentinelRef, headerBlockRef, contentSentinelRef, pinnedHeader })}
        </PageStickyContext.Provider>
    );
};
